import { defineComponent, ref, h, onMounted, onBeforeUnmount, openBlock, createBlock, unref, normalizeClass, withCtx, createElementVNode, createElementBlock, Fragment, renderList } from 'vue';
import { APPEARANCE, VARIANT, SHAPE, SIZE } from '@omnica/button';

var script = /*#__PURE__*/ defineComponent({
    __name: 'UiButton',
    props: {
        /** Устанавливает тип кнопки */
        type: {
            type: String,
            default: 'button',
        },
        /** Устанавливает атрибут href якоря */
        href: {
            type: String,
            default: undefined,
        },
        /** Регулирует внешний вид кнопки: primary, secondary, tertiary or outlined */
        appearance: {
            type: String,
            default: APPEARANCE.PRIMARY,
        },
        /** Регулирует цветовую схему кнопки: default, success or danger */
        variant: {
            type: String,
            default: VARIANT.DEFAULT,
        },
        /** Закругленная или прямоугольная */
        shape: {
            type: String,
            default: SHAPE.DEFAULT,
        },
        /** Размер */
        size: {
            type: String,
            default: SIZE.SM,
        },
        /** Если кнопка активна */
        active: {
            type: Boolean,
            default: false,
        },
        /** Определяет, будет ли текст обрезаться через многоточие или переноситься на следующую строку */
        ellipsis: {
            type: Boolean,
            default: true,
        },
        /** Если кнопка отключена */
        disabled: {
            type: Boolean,
            default: false,
        },
        /** Если кнопка заблокирована */
        locked: {
            type: Boolean,
            default: false,
        },
    },
    setup(__props, { expose }) {
        const props = __props;
        const SHAPE_FLAG_ARRAY_CHILDREN = 16;
        const SHAPE_FLAG_TEXT = 8;
        const inlines = ['b', 'i', 'span', 'strong'];
        const is = (node, type) => node.type.toString() === type;
        const isComment = (node) => is(node, 'Symbol(Comment)');
        const isTextual = (node) => [...inlines, 'Symbol(Text)', 'Symbol()'].some(t => is(node, t)) || node.shapeFlag === SHAPE_FLAG_TEXT;
        const UiButtonRoot = defineComponent({
            name: 'UiButtonRoot',
            props: {
                href: {
                    type: String,
                    default: undefined,
                },
            },
            setup(props, { attrs, slots, expose }) {
                const root = ref(null);
                const focus = () => { var _a; return (_a = root.value) === null || _a === void 0 ? void 0 : _a.focus(); };
                expose({ focus });
                return () => h(props.href ? 'a' : 'button', Object.assign(Object.assign(Object.assign({}, attrs), (props.href ? { href: props.href } : {})), { ref: root }), slots);
            },
        });
        const UiButtonContent = (props) => {
            if (props.node.shapeFlag === SHAPE_FLAG_ARRAY_CHILDREN) {
                return props.node.children.map(node => UiButtonContent({ node, shape: props.shape }));
            }
            const isText = isTextual(props.node);
            if (isComment(props.node) || (isText && props.shape === SHAPE.SQUARE)) {
                return;
            }
            return h('span', {
                class: {
                    ['omnica-button__icon']: !isText,
                    ['omnica-button__text']: isText,
                },
            }, Object.assign({}, props.node));
        };
        const hasLeadingIcon = ref(false);
        const hasTrailingIcon = ref(false);
        const hasText = ref(false);
        const root = ref(null);
        expose({
            focus: () => { var _a; return (_a = root.value) === null || _a === void 0 ? void 0 : _a.focus(); },
        });
        const inner = ref(null);
        const updateFlags = () => {
            var _a, _b;
            const types = Array.from((_b = (_a = inner.value) === null || _a === void 0 ? void 0 : _a.children) !== null && _b !== void 0 ? _b : []).map(el => {
                return inlines.includes(el.tagName.toLowerCase()) || el.nodeType === Node.TEXT_NODE
                    ? 'text'
                    : 'icon';
            });
            hasLeadingIcon.value = props.shape !== SHAPE.SQUARE && types[0] === 'icon';
            hasTrailingIcon.value = props.shape !== SHAPE.SQUARE && types[types.length - 1] === 'icon';
            hasText.value = types.some(t => t === 'text');
        };
        let observer = undefined;
        onMounted(() => {
            var _a;
            observer = new MutationObserver((mutations) => {
                for (const mutation of mutations) {
                    if (['childList', 'subtree'].includes(mutation.type)) {
                        updateFlags();
                    }
                }
            });
            observer.observe((_a = root.value) === null || _a === void 0 ? void 0 : _a.$el, { childList: true, subtree: true });
            updateFlags();
        });
        onBeforeUnmount(() => {
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            observer = undefined;
        });
        return (_ctx, _cache) => {
            return (openBlock(), createBlock(unref(UiButtonRoot), {
                ref_key: "root",
                ref: root,
                type: !__props.href ? __props.type : null,
                href: __props.href,
                class: normalizeClass({
                    ['omnica-button']: true,
                    [`omnica-button_${__props.appearance}`]: true,
                    [`omnica-button_${__props.size}`]: true,
                    [`omnica-button_${__props.shape}`]: true,
                    [`omnica-button_${__props.variant}`]: true,
                    ['omnica-button_has-leading-icon']: hasText.value && hasLeadingIcon.value,
                    ['omnica-button_has-trailing-icon']: hasText.value && hasTrailingIcon.value,
                    ['omnica-button_active']: __props.active,
                    ['omnica-button_disabled']: __props.disabled,
                    ['omnica-button_ellipsis']: __props.ellipsis,
                    ['omnica-button_locked']: __props.locked,
                }),
                disabled: __props.disabled || __props.locked
            }, {
                default: withCtx(() => [
                    createElementVNode("span", {
                        ref_key: "inner",
                        ref: inner,
                        class: "omnica-button__inner"
                    }, [
                        (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.$slots.default ? _ctx.$slots.default() : [], (node, index) => {
                            return (openBlock(), createBlock(UiButtonContent, {
                                key: index,
                                node: node,
                                shape: __props.shape
                            }, null, 8 /* PROPS */, ["node", "shape"]));
                        }), 128 /* KEYED_FRAGMENT */))
                    ], 512 /* NEED_PATCH */)
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["type", "href", "class", "disabled"]));
        };
    }
});

script.__file = "packages/button/src/UiButton.vue";

export { script as default };
